import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage, Language } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';
import { trackContactClick } from '@/lib/trackContactClick';
import logoRockLine from '@/assets/logo-rockline.png';
const Ikurrina = () => (
  <svg viewBox="0 0 30 20" className="h-4 w-5 rounded-sm" aria-hidden="true">
    <rect width="30" height="20" fill="#D52B1E" />
    <line x1="0" y1="10" x2="30" y2="10" stroke="white" strokeWidth="4" />
    <line x1="15" y1="0" x2="15" y2="20" stroke="white" strokeWidth="4" />
    <line x1="0" y1="0" x2="30" y2="20" stroke="#009B48" strokeWidth="2.5" />
    <line x1="30" y1="0" x2="0" y2="20" stroke="#009B48" strokeWidth="2.5" />
  </svg>
);

const SpainFlag = () => (
  <svg viewBox="0 0 30 20" className="h-4 w-5 rounded-sm" aria-hidden="true">
    <rect width="30" height="5" fill="#AA151B" />
    <rect y="5" width="30" height="10" fill="#F1BF00" />
    <rect y="15" width="30" height="5" fill="#AA151B" />
  </svg>
);

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const {
    language,
    setLanguage,
    t
  } = useLanguage();
  const location = useLocation();
  const navItems = [{
    path: '/',
    label: t('nav.home')
  }, {
    path: '/sobre-nosotros',
    label: t('nav.about')
  }, {
    path: '/servicios',
    label: t('nav.services')
  }, {
    path: '/proyectos',
    label: t('nav.projects')
  }, {
    path: '/presupuesto',
    label: t('nav.quote')
  }, {
    path: '/inspeccion-de-rocodromos',
    label: t('nav.inspection')
  }, {
    path: '/blog',
    label: t('nav.blog')
  }, {
    path: '/contacto',
    label: t('nav.contact')
  }];
  const isActive = (path: string) => location.pathname === path;
  const toggleLanguage = () => {
    setLanguage(language === 'es' ? 'eu' : 'es');
  };
  return <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img alt="Rock-Line.es" className="h-12 md:h-14 w-auto" src="/lovable-uploads/c0f4e829-1105-48f1-8289-5cf01dbbb95d.png" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map(item => <Link key={item.path} to={item.path} className={cn('px-4 py-2 rounded-md text-sm font-medium transition-colors', isActive(item.path) ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted')}>
                {item.label}
              </Link>)}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Language Toggle */}
            <Button variant="outline" size="sm" onClick={toggleLanguage} className="font-medium flex items-center gap-1.5">
              {language === 'es' ? <><Ikurrina /> EU</> : 'ES'}
            </Button>

            {/* Phone CTA */}
            <Button asChild size="sm">
              <a href="tel:+34669568722" onClick={() => trackContactClick('phone')} className="flex items-center gap-2">
                <Phone className="h-4 w-4" />
                <span className="hidden xl:inline">669 56 87 22</span>
              </a>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex lg:hidden items-center gap-2">
            <Button variant="outline" size="sm" onClick={toggleLanguage} className="font-medium flex items-center gap-1.5">
              {language === 'es' ? <><Ikurrina /> EU</> : 'ES'}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Toggle menu">
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && <nav className="lg:hidden py-4 border-t border-border animate-fade-in">
            <div className="flex flex-col gap-2">
              {navItems.map(item => <Link key={item.path} to={item.path} onClick={() => setIsMenuOpen(false)} className={cn('px-4 py-3 rounded-md text-base font-medium transition-colors', isActive(item.path) ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted')}>
                  {item.label}
                </Link>)}
              <a href="tel:+34669568722" onClick={() => trackContactClick('phone')} className="flex items-center gap-2 px-4 py-3 text-primary font-medium">
                <Phone className="h-5 w-5" />
                669 56 87 22
              </a>
            </div>
          </nav>}
      </div>
    </header>;
};
export default Header;